import {
    BlogRollWidgetData, BlogRollWidgetLayout, IBlogRollWidgetData, IBlogRollWidgetDataShort
} from "@pageBuilder/typings";
import { Base64Util } from "../../../utils/base64-util";
import { BlogRollScriptRenderer } from "./blog-roll-script-renderer";
import { BlogRollStylesRenderer } from "./blog-roll-styles-renderers";
import { BlogRollPaginationStylesRenderer } from "./blog-roll-pagination-styles-renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { IRenderingCommonContext } from "@commonRendering/types";

export class BlogRollWidgetRenderer extends WidgetRendererBase<IBlogRollWidgetData> {

    private readonly DATA_ATTRIBUTE = "data-blog-roll";
    private readonly scriptRenderer: BlogRollScriptRenderer;
    private readonly stylesRenderer: BlogRollStylesRenderer;
    private readonly paginationStylesRenderer: BlogRollPaginationStylesRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new BlogRollScriptRenderer();
        this.stylesRenderer = new BlogRollStylesRenderer(this.styleService);
        this.paginationStylesRenderer = new BlogRollPaginationStylesRenderer(this.styleService);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("blogRollWidget");
    }

    protected override rerenderInternal(element: HTMLElement, data: IBlogRollWidgetData): HTMLElement {
        return this.renderCore(element, data, true);
    }

    protected renderInternal(element: HTMLElement, data: IBlogRollWidgetData): HTMLElement {
        return this.renderCore(element, data);
    }

    private renderCore(element: HTMLElement, data: IBlogRollWidgetData, force?: boolean): HTMLElement {
        const existingData = this.getData(element);

        if (existingData) {
            this.clearPreviousLayoutStyles(element, existingData, data);
        }

        this.setAttributes(element, data);
        this.setLayoutClass(element, data.layout);

        this.scriptRenderer.renderCommon(this.renderedDocument);

        if (force || !BlogRollWidgetData.localScriptRerenderNeeded(existingData, data)) {
            this.scriptRenderer.renderLocal(element);
        }

        this.renderStyles(element, data);

        return element;
    }

    protected renderStyles(element: HTMLElement, data: IBlogRollWidgetData): void {
        this.renderBaseStyles(element, data);
        this.renderPaginationStyles(element, data);
    }

    private clearPreviousLayoutStyles(element: HTMLElement, previousData: IBlogRollWidgetData, data: IBlogRollWidgetData): void {
        const previousLayout = previousData.layout;

        if (previousLayout) {
            const layoutChanged = previousLayout !== data.layout;
            this.stylesRenderer.cleanup(element, previousLayout);
            if (layoutChanged) {
                this.stylesRenderer.partialUpdateWithDefaults(data);
            }
        }
    }

    protected renderBaseStyles(element: HTMLElement, data: IBlogRollWidgetData): void {
        this.stylesRenderer.render(element, data.style, data.layout);
        this.animationRenderer.setAnimationStyles(element, data.style.animation);
    }

    protected renderPaginationStyles(element: HTMLElement, data: IBlogRollWidgetData): void {
        this.paginationStylesRenderer.render(element, data.pagination.style);
    }

    private setAttributes(element: HTMLElement, data: IBlogRollWidgetData): void {
        const dataForSave = this.prepareDataForSave(data);
        const json = Base64Util.serialize(dataForSave) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
        element.toggleAttribute("data-primary-element", data.primaryElement);
        element.dataset.blogId = `${data.blogId || ""}`;
        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }
    }

    private prepareDataForSave(data: IBlogRollWidgetData): IBlogRollWidgetDataShort {
        return {
            showAuthorName: data.showAuthorName,
            showCategories : data.showCategories,
            showDate: data.showDate,
            showDescription: data.showDescription,
            withoutImage: data.withoutImage,
            layout: data.layout,
            imgRatio: data.imgRatio,
            paginationType: data.pagination.type,
            articlesPerPage: data.pagination.articlesPerPage,
            showPagination: data.pagination.showPagination,
            defaultCategory: data.defaultCategory
        };
    }

    private setLayoutClass(element: HTMLElement, layout?: BlogRollWidgetLayout): void {
        this.clearLayoutClasses(element);

        if (!layout) {
            return;
        }

        element.classList.toggle(`layout-${layout}`.toLowerCase(), true);
    }

    private clearLayoutClasses(element: HTMLElement): void {
        const layoutClasses: string[] = [];
        element.classList.forEach(className => {
            if (className.startsWith("layout-")) {
                layoutClasses.push(className);
            }
        });

        element.classList.remove(...layoutClasses);
    }

    private getShortData(element: HTMLElement): IBlogRollWidgetDataShort {
        return Base64Util.deserialize(element.getAttribute(this.DATA_ATTRIBUTE)) as IBlogRollWidgetDataShort;
    }

    private getLayout(element: HTMLElement): BlogRollWidgetLayout | null {
        return this.getShortData(element)?.layout;
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        const styles = this.paginationStylesRenderer.getStyleSelectors(element);
        const layout = this.getLayout(element);
        if (layout) {
            styles.push(...this.stylesRenderer.getContentSelectorsInternal(element, layout));
        }

        return styles;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        if (!document.querySelector("[data-widget=blogRollWidget]")) {
            this.scriptRenderer.cleanupCommon(this.renderedDocument);
        }
    }

}
